// BL-177 Step 2l / Step 2j (E): "What colleagues can see about my conversations" (Settings > Communications > Conversation privacy).
// A 3-position 3D switch: Busy / Limited (default) / Full. The help for every position stays visible, the chosen one is marked.
// The database enforces it for everyone, owners, admins and platform admins included. It lives ONLY in Settings.
import React, { useEffect, useId, useRef, useState } from 'react';
import { PRIVACY_LEVELS, DEFAULT_PRIVACY_LEVEL, loadMyPrivacy, saveMyPrivacy } from '../../lib/conversationAccess';
import { supabaseClient } from '../../config/supabase';
import SegmentedSwitch3D from '../ui/SegmentedSwitch3D';

const SHORT_LABELS = { busy: 'Busy', limited: 'Limited', full: 'Full' };
const OPTIONS = PRIVACY_LEVELS.map((item) => ({ value: item.value, label: SHORT_LABELS[item.value] || item.label }));

export default function ConversationPrivacyCard({ userId, orgId, client = supabaseClient }) {
    const ids = useId();
    const [level, setLevel] = useState(DEFAULT_PRIVACY_LEVEL);
    const [status, setStatus] = useState('');
    const [failed, setFailed] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const saved = useRef(DEFAULT_PRIVACY_LEVEL);
    useEffect(() => {
        let active = true;
        if (!userId) return () => { active = false; };
        loadMyPrivacy(client, userId).then((value) => { if (active) { saved.current = value; setLevel(value); setLoaded(true); } });
        return () => { active = false; };
    }, [client, userId]);

    async function choose(next) {
        const previous = saved.current;
        setLevel(next);
        setStatus('Saving…');
        setFailed(false);
        try {
            await saveMyPrivacy(client, { userId, orgId, level: next });
            saved.current = next;
            setStatus('Saved');
        } catch (failure) {
            setLevel(previous);
            setFailed(true);
            setStatus(failure?.message || 'Could not save this. Please try again.');
        }
    }

    return (
        <section aria-labelledby={`${ids}-title`} className="flex flex-col gap-3" data-conversation-privacy="true">
            <p id={`${ids}-title`} className="sr-only">What colleagues can see about my conversations</p>
            <p className="text-sm">This applies to conversations in your My inbox. Owners and admins follow it too: nobody gets around it.</p>
            <SegmentedSwitch3D
                labelledBy={`${ids}-title`}
                describedBy={`${ids}-help`}
                options={OPTIONS}
                value={level}
                disabled={!loaded}
                onChange={choose}
                testId="conversation-privacy-switch"
            />
            <ul id={`${ids}-help`} className="flex flex-col gap-2 text-sm" data-testid="conversation-privacy-help">
                {PRIVACY_LEVELS.map((item) => (
                    <li key={item.value} data-privacy-help={item.value} data-chosen={level === item.value ? 'true' : undefined}
                        className={`rounded-lg border p-2.5 ${level === item.value ? 'border-[var(--selected-pill-border)] bg-[var(--surface-sunken)]' : 'border-[var(--surface-border)]'}`}>
                        <span className="font-bold">{SHORT_LABELS[item.value] || item.label}{item.value === DEFAULT_PRIVACY_LEVEL ? ' (default)' : ''}</span>
                        {': '}{item.help}
                    </li>
                ))}
            </ul>
            <p role={failed ? 'alert' : 'status'} aria-live="polite" className="min-h-[1.25rem] text-sm font-semibold" data-testid="conversation-privacy-status">{status}</p>
        </section>
    );
}
